import React from 'react';

import { Drawer, Empty } from 'antd';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import IntlMessages from '@src/util/IntlMessages';

import type { ActiveUserExecutionDetail, ExecutionComplianceType } from '../types';
import styles from './ActiveUserComplianceDrawer.module.less';

interface ActiveUserComplianceDrawerProps {
  userName: string;
  type: ExecutionComplianceType | null;
  executions: ActiveUserExecutionDetail[];
  open: boolean;
  isMobile: boolean;
  onClose: () => void;
}

const titleIdByType: Record<ExecutionComplianceType, string> = {
  schedule: 'activeUserDashboard.schedule.list.column',
  task: 'activeUserDashboard.task.list.column',
  checkpoint_route: 'activeUserDashboard.checkpointRoute.list.column',
};

const ActiveUserComplianceDrawer: React.FC<ActiveUserComplianceDrawerProps> = ({
  userName,
  type,
  executions,
  open,
  isMobile,
  onClose,
}) => {
  const intl = useIntl();
  const title = type
    ? `${intl.formatMessage({ id: titleIdByType[type] })} · ${userName}`
    : '';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 560}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer title={<Title.Header value={title} />} goBack={onClose} />
        </BoxContainer>
        <BoxContainer content>
          {executions.length ? (
            <div className={styles.executionList}>
              {executions.map(execution => (
                <section className={styles.execution} key={execution.id}>
                  <header className={styles.executionHeader}>
                    <strong>{execution.name}</strong>
                    <span className={styles.total}>
                      {execution.finished}/{execution.expected}
                    </span>
                  </header>
                  {execution.triggers.length > 0 && (
                    <div className={styles.triggers}>
                      {execution.triggers.map(trigger => (
                        <div className={styles.trigger} key={trigger.id}>
                          <span>{trigger.name}</span>
                          <span className={classNames(styles.total, trigger.finished === trigger.expected && styles.complete)}>
                            {trigger.finished}/{trigger.expected}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          ) : (
            <Empty description={<IntlMessages id="activeUserDashboard.compliance.empty" />} />
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default React.memo(ActiveUserComplianceDrawer);
